import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Users, Briefcase, Scale, Plus, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePopup } from '@/context/PopupContext';

const servicesData = [
  {
    icon: Shield,
    title: 'Trademark Services',
    description: 'Register Your Trademark, Clearance Search, Ownership Transfers, Office Actions, Trademark Monitoring, and TTAB Petition services to protect and maintain your brand identity effectively.',
    link: '/services/trademark-registration',
    gradient: 'from-purple-500 to-purple-600'
  },
  {
    icon: Briefcase,
    title: 'Company Formation Services',
    description: 'LLC Formation, C-Corporation, S-Corporation, and Non-Profit Formation services designed to help you establish your business structure with legal compliance and professional guidance.',
    link: '/services/company-formation',
    gradient: 'from-purple-500 to-purple-600'
  },
  {
    icon: FileText,
    title: 'Copyright Services',
    description: 'Register Copyright, Copyright Enforcement, Copyright Transfers, Copyright Legal, Portfolio Management, and DMCA Compliance to secure your creative works and intellectual property rights.',
    link: '/services/copyright-registration',
    gradient: 'from-purple-500 to-purple-600'
  },
  {
    icon: Scale,
    title: 'Patent Services',
    description: 'Existing Patent Search, Response to Patent Office Actions, Patent Application Submission, and Legal Representation for Patent Protection to safeguard your innovations and technical inventions.',
    link: '/services/patent-services',
    gradient: 'from-purple-500 to-purple-600'
  },
  {
    icon: Users,
    title: 'Legal Services',
    description: 'Legal Consultation on Partnerships & LLPs, Due Diligence, Business Immigration Law, IT & Commercial Law, and Data Protection & Privacy Law for comprehensive business legal support.',
    link: '/services/legal-services',
    gradient: 'from-purple-500 to-purple-600'
  },
  {
    icon: Plus,
    title: 'Additional Services',
    description: 'Amazon Brand Registry (US), Business Formation Services, Company Dissolution, Certificate of Good Standing, Tax Advisory & Filing, and CFO Services for extended business needs.',
    link: '/services/additional-services',
    gradient: 'from-purple-500 to-purple-600'
  }
];

export const ServicesOverviewPage = () => {
  const { openPopup } = usePopup();

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-950 via-indigo-900 to-purple-950 py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
            Our Comprehensive Services
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Complete intellectual property protection and business formation solutions tailored to your needs
          </p>
          <Button
            onClick={() => openPopup('services')}
            size="lg"
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-6 text-lg"
          >
            Get Started
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-purple-300 group"
              >
                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>

                {/* Description in Paragraph Format */}
                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* View Services Link */}
                <Link
                  to={service.link}
                  className="inline-flex items-center text-purple-600 font-semibold hover:text-purple-700 transition-colors group-hover:underline"
                >
                  View Services
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Ready to Protect Your Business?
          </h2>
          <p className="text-lg text-purple-100 mb-8">
            Get started with our expert services today and secure your intellectual property
          </p>
          <Button
            onClick={() => openPopup('services-cta')}
            size="lg"
            className="bg-white text-purple-700 hover:bg-purple-50 font-semibold text-lg px-8 py-6 transition-transform hover:scale-105 shadow-xl"
          >
            Start Your Application
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};
